import { DayKey } from './types';

/**
 * Gaming days run midnight to midnight America/Chicago (EN-11153). The
 * server decides which day a flop counts for; the client only needs the
 * current day key and the time left, for last call and the midnight reset.
 */
const TZ = 'America/Chicago';

let partsFormatter: Intl.DateTimeFormat | null = null;

function formatter(): Intl.DateTimeFormat {
  if (!partsFormatter) {
    partsFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
  return partsFormatter;
}

interface CentralParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function centralParts(nowMs: number): CentralParts {
  const out: Record<string, number> = {};
  formatter()
    .formatToParts(new Date(nowMs))
    .forEach((p) => {
      if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
    });
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    hour: out.hour % 24,
    minute: out.minute,
    second: out.second
  };
}

const pad = (n: number) => (n < 10 ? '0' + n : String(n));

export function centralDayKey(nowMs: number): DayKey {
  const p = centralParts(nowMs);
  return `${p.year}${pad(p.month)}${pad(p.day)}`;
}

/**
 * Milliseconds until the next Central midnight. Walks the wall clock rather
 * than assuming 24h days, so the 23h and 25h daylight-saving days are right
 * to within the minute.
 */
export function msUntilCentralMidnight(nowMs: number): number {
  const p = centralParts(nowMs);
  const elapsed = ((p.hour * 60 + p.minute) * 60 + p.second) * 1000 + (nowMs % 1000);
  let remaining = 24 * 3600 * 1000 - elapsed;
  // On a DST day the wall clock gains or loses an hour before midnight.
  const later = centralDayKey(nowMs + remaining);
  if (later === centralDayKey(nowMs)) remaining += 3600 * 1000;
  else if (centralDayKey(nowMs + remaining - 3600 * 1000) !== centralDayKey(nowMs))
    remaining -= 3600 * 1000;
  return Math.max(0, remaining);
}

/** "1h 52m", "45m", "under 1m". */
export function formatTimeLeft(ms: number): string {
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return 'under 1m';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `20261003` → "Oct 3". */
export function formatDayKey(day: DayKey): string {
  const m = parseInt(day.slice(4, 6), 10);
  const d = parseInt(day.slice(6, 8), 10);
  return `${MONTHS[m - 1] || ''} ${d}`;
}

/** The day before `day`, as a key. */
export function previousDayKey(day: DayKey): DayKey {
  const y = parseInt(day.slice(0, 4), 10);
  const m = parseInt(day.slice(4, 6), 10);
  const d = parseInt(day.slice(6, 8), 10);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}`;
}
